import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { MagnifyingGlass, Funnel, FloppyDisk, Trash, Buildings, UsersThree, CurrencyDollar, Folder, ArrowRight, X } from '@phosphor-icons/react';
import { globalSearch, advancedSearch, getSavedFilters, saveFilter, deleteSavedFilter } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const MODULE_OPTIONS = [
  { value: 'all', label: 'Tous', icon: MagnifyingGlass },
  { value: 'organizations', label: 'Organisations', icon: Buildings },
  { value: 'contacts', label: 'Contacts', icon: UsersThree },
  { value: 'payments', label: 'Paiements', icon: CurrencyDollar },
  { value: 'dossiers', label: 'Dossiers', icon: Folder },
];

const MODULE_COLORS = {
  organizations: '#2D5A4C',
  contacts: '#00AEEF',
  payments: '#FF8E00',
  dossiers: '#D5007D',
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [module, setModule] = useState('all');
  const [quickResults, setQuickResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedModule, setAdvancedModule] = useState('organizations');
  const [advancedResults, setAdvancedResults] = useState([]);
  const [advancedTotal, setAdvancedTotal] = useState(0);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveDialog, setSaveDialog] = useState(false);
  const [filterName, setFilterName] = useState('');

  // Advanced filter state
  const [filters, setFilters] = useState({
    q: '', territoire: '', statut: '', type_acteur: '', type_flux: '',
    montant_min: '', montant_max: '', date_from: '', date_to: '', membre_ftpe: '',
  });

  useEffect(() => { fetchSavedFilters(); }, []);

  const fetchSavedFilters = async () => {
    try {
      const res = await getSavedFilters();
      setSavedFilters(res.data.items || []);
    } catch {}
  };

  // Quick search with debounce
  const doQuickSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setQuickResults([]); return; }
    setLoading(true);
    try {
      const res = await globalSearch(q, module);
      setQuickResults(res.data.results || []);
    } catch {} finally { setLoading(false); }
  }, [module]);

  useEffect(() => {
    const timer = setTimeout(() => doQuickSearch(query), 350);
    return () => clearTimeout(timer);
  }, [query, doQuickSearch]);

  const handleAdvancedSearch = async () => {
    setAdvancedLoading(true);
    try {
      const params = { module: advancedModule, ...filters };
      const res = await advancedSearch(params);
      setAdvancedResults(res.data.items || []);
      setAdvancedTotal(res.data.total || 0);
    } catch {} finally { setAdvancedLoading(false); }
  };

  const handleSaveFilter = async () => {
    if (!filterName) return;
    try {
      await saveFilter({ name: filterName, module: advancedModule, filters });
      toast.success('Filtre sauvegarde');
      setSaveDialog(false);
      setFilterName('');
      fetchSavedFilters();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteFilter = async (id) => {
    try {
      await deleteSavedFilter(id);
      toast.success('Filtre supprime');
      fetchSavedFilters();
    } catch { toast.error('Erreur'); }
  };

  const applyFilter = (f) => {
    setAdvancedModule(f.module || 'organizations');
    setFilters(f.filters || {});
    setShowAdvanced(true);
    setTimeout(() => handleAdvancedSearch(), 200);
  };

  const navigateToResult = (r) => {
    const routes = { organizations: '/organizations', contacts: '/contacts', payments: '/payments', dossiers: '/dossiers' };
    navigate(`${routes[r.module] || '/'}/${r.id}`);
  };

  const resetFilters = () => {
    setFilters({ q: '', territoire: '', statut: '', type_acteur: '', type_flux: '', montant_min: '', montant_max: '', date_from: '', date_to: '', membre_ftpe: '' });
    setAdvancedResults([]);
    setAdvancedTotal(0);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="search-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Recherche avancée"
        subtitle="Requêtes multi-modules avec filtres combinés"
        icon={<MagnifyingGlass size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#00AEEF', '#D5007D']}
      />

      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Recherche avancee</h1>
        <p className="text-sm text-[#5E6D66] mt-1">Recherche globale multi-modules avec filtres sauvegardables</p>
      </div>

      {/* Quick search bar */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher par nom, email, reference, SIREN..."
                className="pl-10 border-[#E8E6E1] text-sm"
                data-testid="search-input"
              />
              {query && (
                <button onClick={() => { setQuery(''); setQuickResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E6D66]">
                  <X size={14} />
                </button>
              )}
            </div>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-[180px] border-[#E8E6E1] text-xs" data-testid="search-module-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODULE_OPTIONS.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-xs">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showAdvanced ? 'default' : 'outline'}
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={showAdvanced ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : 'border-[#E8E6E1]'}
              data-testid="toggle-advanced-btn"
            >
              <Funnel size={16} className="mr-1.5" /> Filtres
            </Button>
          </div>

          {/* Quick results */}
          {(loading || quickResults.length > 0) && query.length >= 2 && (
            <div className="mt-3 border-t border-[#E8E6E1] pt-3 space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="quick-results">
              {loading ? (
                <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#2D5A4C]" /></div>
              ) : quickResults.map((r) => {
                const Icon = MODULE_OPTIONS.find(m => m.value === r.module)?.icon || MagnifyingGlass;
                const color = MODULE_COLORS[r.module] || '#5E6D66';
                return (
                  <div
                    key={`${r.module}-${r.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F9F8F5] cursor-pointer transition-colors"
                    onClick={() => navigateToResult(r)}
                    data-testid={`result-${r.module}-${r.id}`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
                      <Icon size={14} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#1A2421] truncate">{r.title}</p>
                      <p className="text-[10px] text-[#5E6D66] truncate">{r.subtitle}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px]" style={{ borderColor: color, color }}>
                      {MODULE_OPTIONS.find(m => m.value === r.module)?.label}
                    </Badge>
                    <ArrowRight size={12} className="text-[#5E6D66]" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved filters */}
      {savedFilters.length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="saved-filters">
          {savedFilters.map(f => (
            <div key={f.id} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F0EFEB] border border-[#E8E6E1] text-xs cursor-pointer hover:bg-[#E8E6E1] transition-colors group" data-testid={`saved-filter-${f.id}`}>
              <button onClick={() => applyFilter(f)} className="text-[#1A2421] font-medium">{f.name}</button>
              <Badge variant="outline" className="text-[9px] ml-1" style={{ borderColor: MODULE_COLORS[f.module], color: MODULE_COLORS[f.module] }}>
                {MODULE_OPTIONS.find(m => m.value === f.module)?.label}
              </Badge>
              <button onClick={(e) => { e.stopPropagation(); handleDeleteFilter(f.id); }} className="ml-1 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Advanced filters */}
      {showAdvanced && (
        <Card className="border-[#E8E6E1]" data-testid="advanced-filters-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Filtres avances</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-[#5E6D66] text-xs h-7">Reinitialiser</Button>
                <Button variant="outline" size="sm" onClick={() => setSaveDialog(true)} className="border-[#2D5A4C] text-[#2D5A4C] text-xs h-7" data-testid="save-filter-btn">
                  <FloppyDisk size={12} className="mr-1" /> Sauvegarder
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] text-[#5E6D66]">Module</Label>
                <Select value={advancedModule} onValueChange={setAdvancedModule}>
                  <SelectTrigger className="h-8 text-xs" data-testid="adv-module"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organizations" className="text-xs">Organisations</SelectItem>
                    <SelectItem value="contacts" className="text-xs">Contacts</SelectItem>
                    <SelectItem value="payments" className="text-xs">Paiements</SelectItem>
                    <SelectItem value="dossiers" className="text-xs">Dossiers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[#5E6D66]">Texte</Label>
                <Input value={filters.q} onChange={(e) => setFilters(p => ({ ...p, q: e.target.value }))} placeholder="Recherche..." className="h-8 text-xs" data-testid="adv-q" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[#5E6D66]">Territoire</Label>
                <Input value={filters.territoire} onChange={(e) => setFilters(p => ({ ...p, territoire: e.target.value }))} placeholder="Ex: Guadeloupe" className="h-8 text-xs" data-testid="adv-territoire" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[#5E6D66]">Statut</Label>
                <Input value={filters.statut} onChange={(e) => setFilters(p => ({ ...p, statut: e.target.value }))} placeholder="Ex: en_attente" className="h-8 text-xs" data-testid="adv-statut" />
              </div>
            </div>
            {advancedModule === 'payments' && (
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] text-[#5E6D66]">Montant min</Label>
                  <Input type="number" value={filters.montant_min} onChange={(e) => setFilters(p => ({ ...p, montant_min: e.target.value }))} className="h-8 text-xs" data-testid="adv-montant-min" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-[#5E6D66]">Montant max</Label>
                  <Input type="number" value={filters.montant_max} onChange={(e) => setFilters(p => ({ ...p, montant_max: e.target.value }))} className="h-8 text-xs" data-testid="adv-montant-max" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-[#5E6D66]">Date debut</Label>
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))} className="h-8 text-xs" data-testid="adv-date-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-[#5E6D66]">Date fin</Label>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))} className="h-8 text-xs" data-testid="adv-date-to" />
                </div>
              </div>
            )}
            {advancedModule === 'contacts' && (
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] text-[#5E6D66]">Type d'acteur</Label>
                  <Input value={filters.type_acteur} onChange={(e) => setFilters(p => ({ ...p, type_acteur: e.target.value }))} placeholder="Ex: porteur_projet" className="h-8 text-xs" data-testid="adv-type-acteur" />
                </div>
              </div>
            )}
            <Button onClick={handleAdvancedSearch} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="adv-search-btn">
              <MagnifyingGlass size={14} className="mr-1.5" /> Rechercher
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Advanced results */}
      {showAdvanced && (advancedLoading || advancedResults.length > 0) && (
        <Card className="border-[#E8E6E1]" data-testid="advanced-results-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>
              Resultats ({advancedTotal})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {advancedLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E8E6E1]">
                    {advancedModule === 'organizations' && (
                      <>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Raison sociale</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Email</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Territoire</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                      </>
                    )}
                    {advancedModule === 'contacts' && (
                      <>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Nom</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Email</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Telephone</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                      </>
                    )}
                    {advancedModule === 'payments' && (
                      <>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Reference</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Montant</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Echeance</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                      </>
                    )}
                    {advancedModule === 'dossiers' && (
                      <>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Numero</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                        <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                      </>
                    )}
                    <TableHead className="text-[10px] uppercase text-[#5E6D66] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advancedResults.map((item) => (
                    <TableRow key={item.id} className="border-[#E8E6E1] hover:bg-[#FAFAF8] cursor-pointer" onClick={() => navigate(`/${advancedModule}/${item.id}`)}>
                      {advancedModule === 'organizations' && (
                        <>
                          <TableCell className="text-xs font-medium text-[#1A2421]">{item.raison_sociale}</TableCell>
                          <TableCell className="text-xs text-[#00AEEF]">{item.email || '-'}</TableCell>
                          <TableCell className="text-xs text-[#5E6D66]">{item.territoire || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.statut}</Badge></TableCell>
                        </>
                      )}
                      {advancedModule === 'contacts' && (
                        <>
                          <TableCell className="text-xs font-medium text-[#1A2421]">{item.prenom} {item.nom}</TableCell>
                          <TableCell className="text-xs text-[#00AEEF]">{item.email || '-'}</TableCell>
                          <TableCell className="text-xs text-[#5E6D66]">{item.telephone || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.type_acteur}</Badge></TableCell>
                        </>
                      )}
                      {advancedModule === 'payments' && (
                        <>
                          <TableCell className="text-xs font-mono text-[#1A2421]">{item.reference_interne || '-'}</TableCell>
                          <TableCell className="text-xs font-medium text-[#1A2421]">{(item.montant_attendu || 0).toLocaleString('fr-FR')} €</TableCell>
                          <TableCell className="text-xs text-[#5E6D66]">{item.date_echeance || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.statut}</Badge></TableCell>
                        </>
                      )}
                      {advancedModule === 'dossiers' && (
                        <>
                          <TableCell className="text-xs font-mono text-[#1A2421]">{item.numero || '-'}</TableCell>
                          <TableCell className="text-xs text-[#5E6D66]">{item.type_accompagnement || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.statut}</Badge></TableCell>
                        </>
                      )}
                      <TableCell className="text-right"><ArrowRight size={14} className="text-[#5E6D66] inline" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save filter dialog */}
      <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="save-filter-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Sauvegarder le filtre</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nom du filtre</Label>
              <Input value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="Ex: Orgs Guadeloupe en retard" className="text-xs" data-testid="filter-name-input" />
            </div>
            <p className="text-[10px] text-[#5E6D66]">Module: {MODULE_OPTIONS.find(m => m.value === advancedModule)?.label}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveFilter} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="confirm-save-filter">Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
